import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import type { EmailThread, EmailThreadPage } from './internalEmail'

export interface QaSendPayload {
  fromEmail: string
  toEmail: string
  subject: string
  body: string
  attachments: File[]
  threadKey?: string
}

export interface QaSendResult {
  outcome: string
  handler: string | null
  threadKey: string | null
}

function sendQaEmail(payload: QaSendPayload): Promise<QaSendResult> {
  const form = new FormData()
  form.append('from_email', payload.fromEmail)
  form.append('to_email', payload.toEmail)
  form.append('subject', payload.subject)
  form.append('body', payload.body)
  if (payload.threadKey) form.append('thread_key', payload.threadKey)
  for (const file of payload.attachments) form.append('attachments', file)
  return axios.post('/api/v1/insure/qa-emails/send/', form).then((r) => toCamelCase(r.data) as QaSendResult)
}

export function useSendQaEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: sendQaEmail,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.QA_EMAIL_THREADS] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.QA_EMAIL_THREAD] })
    },
  })
}

export function useQaEmailThreads(q: string, page: number) {
  return useQuery({
    queryKey: [CacheKeys.QA_EMAIL_THREADS, q, page],
    queryFn: () =>
      axios
        .get('/api/v1/insure/qa-emails/threads/', { params: { q: q || undefined, page } })
        .then((r) => toCamelCase(r.data) as EmailThreadPage),
    placeholderData: (previous) => previous,
  })
}

/** Polls while the agent may still be working; a QA thread is short-lived. */
export function useQaEmailThread(threadKey: string | null) {
  return useQuery({
    queryKey: [CacheKeys.QA_EMAIL_THREAD, threadKey],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/qa-emails/threads/${encodeURIComponent(threadKey!)}/`)
        .then((r) => toCamelCase(r.data) as EmailThread),
    enabled: !!threadKey,
    refetchInterval: 4000,
  })
}

export function useDeleteQaThread() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (threadKey: string) =>
      axios.delete(`/api/v1/insure/qa-emails/threads/${encodeURIComponent(threadKey)}/`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.QA_EMAIL_THREADS] })
    },
  })
}

export interface QaInbox {
  name: string
  email: string
}

/** The shared inboxes this environment listens on — a hardcoded address here
 *  silently produces DROPPED_UNRESOLVED wherever they are suffixed. */
export function useQaInboxes() {
  return useQuery({
    queryKey: [CacheKeys.QA_EMAIL_INBOXES],
    queryFn: () => axios.get('/api/v1/insure/qa-emails/inboxes/').then((r) => r.data.inboxes as QaInbox[]),
    staleTime: 5 * 60 * 1000,
  })
}
